/**
 * ゲームレジストリ
 * 利用可能なマップとゲーム定義を登録
 */
import type { Game } from 'boardgame.io';
import type { ComponentType } from 'react';
import type { BoardProps } from 'boardgame.io/react';
import { BarbadosGame } from '../game/BarbadosGame';
import { PuertoRicoGame } from '../game/PuertoRicoGame';
import { DetroitBankruptcySoloGame } from '../game/DetroitBankruptcySoloGame';
import { SeattleMetroGame } from '../game/SeattleMetroGame';
import { ActionPanelContainer } from '../components/ActionPanelContainer';
import type { BarbadosGameState, PuertoRicoGameState, DetroitGameState, SeattleGameState } from '../types';

// UI設定のインポート
import { BARBADOS_START_MODAL_CONFIG, BARBADOS_END_SCREEN_CONFIG } from '../../data/map/barbados/config';
import { PUERTO_RICO_START_MODAL_CONFIG, PUERTO_RICO_END_SCREEN_CONFIG } from '../../data/map/puerto_rico/config';
import { DETROIT_START_MODAL_CONFIG, DETROIT_END_SCREEN_CONFIG } from '../../data/map/detroit_bankruptcy_solo/config';
import { SEATTLE_START_MODAL_CONFIG, SEATTLE_END_SCREEN_CONFIG } from '../../data/map/seattle_metro/config';

/**
 * StartModal 設定の型
 *
 * 新マップ追加時はこの型に従って config.ts に設定を定義すること。
 * 共通キーは gameStartCommon から取得、マップ固有キーは gameStart[MapName] から取得。
 */
export interface StartModalConfig {
  /** i18n マップ固有キープレフィックス（例: 'gameStartBarbados'） */
  i18nKey: string;

  /** 勝利条件セクションのスタイル */
  victoryStyle: 'success' | 'warning';

  /** 勝利/終了条件設定（統一構造） */
  victory: {
    /** 終了条件キー（gameStartCommon.gameEnd の値で表示） */
    gameEndKey: string;
    /** 勝利条件キー（オプション、マップ固有キーから取得） */
    winConditionKey?: string;
    /** 追加項目キー（スコア計算、株式買い戻し等） */
    additionalItems?: string[];
  };

  /** 特殊ルールフェーズ設定 */
  phases: Array<{
    num: string;
    ruleKey?: string;
    descKey: string;
    actionsKey?: string;
    extraKeys?: string[];
  }>;

  /** 戦略ヒントキー（tip1〜tip4 はコモンで定義） */
  tips: string[];

  /** 特殊リンク/ヘクス設定（Detroit, Seattle等で使用） */
  specialLinks?: string[];

  /** 難易度選択UI表示（Puerto Rico用） */
  hasDifficultySelection?: boolean;
}

/**
 * スコア評価の閾値と i18n キー
 */
export interface ScoreEvaluation {
  /** この評価が適用される最小スコア */
  minScore: number;
  /** i18n キー（gameEnd.[mapKey].evaluations.[key]） */
  key: string;
}

/**
 * EndScreen 設定の型
 */
export interface EndScreenConfig {
  scoreType: 'cash' | 'income' | 'turns' | 'finalScore';
  shareBuybackCost?: number;
  blackCubePenalty?: number;
  incomeMultiplier?: number;
  sharesPenalty?: number;
  victoryThreshold?: number;
  /** スコア評価（降順でマッチング） */
  scoreEvaluations?: ScoreEvaluation[];
}

/**
 * 難易度レベル（i18n キー用）
 */
export type DifficultyLevel = 'easy' | 'medium' | 'hard';

/**
 * マップ i18n キー（mapSelection.maps のキー）
 */
export type MapI18nKey = 'barbados' | 'puertoRico' | 'detroit' | 'seattle';

/**
 * ゲーム登録エントリ
 *
 * 新マップ追加時はこのエントリを GAME_REGISTRY に追加すること。
 */
export interface GameRegistryEntry<G = any> {
  /** ゲーム定義（Boardgame.io Game） */
  game: Game<G>;
  /** ボードコンポーネント */
  board: ComponentType<BoardProps<G>>;
  /** 表示名 */
  displayName: string;
  /** マップデザイナー名 */
  designer: string;
  /** 難易度レベル（i18n キー） */
  difficulty: DifficultyLevel;
  /** マップ i18n キー（mapSelection.maps のキー） */
  i18nMapKey: MapI18nKey;
  /** API用マップタイプ（スコアサーバー用、例: 'seattle_metro_solo'） */
  apiMapType: string;
  /** プレイヤー数 */
  numPlayers: number;
  /** 開発中フラグ */
  isWIP?: boolean;
  /** StartModal 設定 */
  startModalConfig: StartModalConfig;
  /** EndScreen 設定 */
  endScreenConfig: EndScreenConfig;
}

/**
 * ゲームレジストリ
 * キー: マップID、値: ゲーム登録エントリ
 */
export const GAME_REGISTRY: Record<string, GameRegistryEntry> = {
  'barbados': {
    game: BarbadosGame,
    board: ActionPanelContainer as ComponentType<BoardProps<BarbadosGameState>>,
    displayName: 'Barbados',
    designer: 'Ted Alspach',
    difficulty: 'easy',
    i18nMapKey: 'barbados',
    apiMapType: 'barbados',
    numPlayers: 1,
    isWIP: false,
    startModalConfig: BARBADOS_START_MODAL_CONFIG,
    endScreenConfig: BARBADOS_END_SCREEN_CONFIG
  },
  'puerto-rico': {
    game: PuertoRicoGame,
    board: ActionPanelContainer as unknown as ComponentType<BoardProps<PuertoRicoGameState>>,
    displayName: 'Puerto Rico',
    designer: 'Ted Alspach',
    difficulty: 'easy',
    i18nMapKey: 'puertoRico',
    apiMapType: 'puerto_rico',
    numPlayers: 1,
    isWIP: false,
    startModalConfig: PUERTO_RICO_START_MODAL_CONFIG,
    endScreenConfig: PUERTO_RICO_END_SCREEN_CONFIG
  },
  'detroit-bankruptcy-solo': {
    game: DetroitBankruptcySoloGame,
    board: ActionPanelContainer as unknown as ComponentType<BoardProps<DetroitGameState>>,
    displayName: 'Detroit Bankruptcy Solo',
    designer: 'Chad DeShon',
    difficulty: 'medium',
    i18nMapKey: 'detroit',
    apiMapType: 'detroit_bankruptcy_solo',
    numPlayers: 1,
    isWIP: false,
    startModalConfig: DETROIT_START_MODAL_CONFIG,
    endScreenConfig: DETROIT_END_SCREEN_CONFIG
  },
  'seattle-metro-solo': {
    game: SeattleMetroGame,
    board: ActionPanelContainer as unknown as ComponentType<BoardProps<SeattleGameState>>,
    displayName: 'Seattle Metro Solo',
    designer: 'Ken Kuhn',
    difficulty: 'hard',
    i18nMapKey: 'seattle',
    apiMapType: 'seattle_metro_solo',
    numPlayers: 1,
    isWIP: false,
    startModalConfig: SEATTLE_START_MODAL_CONFIG,
    endScreenConfig: SEATTLE_END_SCREEN_CONFIG
  }
  // 将来の追加マップ:
  // 'western_us': { ... },
  // 'rust_belt': { ... },
};

/**
 * デフォルトマップID
 */
export const DEFAULT_MAP_ID = 'barbados';

/**
 * マップIDからゲーム定義を取得
 * @param mapId - マップID
 * @returns ゲーム登録エントリ（存在しない場合はデフォルト）
 */
export function getGameEntry(mapId: string): GameRegistryEntry {
  return GAME_REGISTRY[mapId] || GAME_REGISTRY[DEFAULT_MAP_ID];
}

/**
 * 利用可能なマップのリストを取得
 * @param includeWIP - WIPマップも含めるか
 * @returns マップ情報（i18nキー含む）
 */
export function getAvailableMaps(includeWIP = false): Array<{
  id: string;
  name: string;
  difficulty: DifficultyLevel;
  i18nMapKey: MapI18nKey;
}> {
  return Object.entries(GAME_REGISTRY)
    .filter(([_, entry]) => includeWIP || !entry.isWIP)
    .map(([id, entry]) => ({
      id,
      name: entry.displayName,
      difficulty: entry.difficulty,
      i18nMapKey: entry.i18nMapKey
    }));
}

/**
 * マップ名からUI設定を取得
 * @param mapName - マップ表示名（G.mapName）
 * @returns StartModal/EndScreen設定 + designer + i18nMapKey
 */
export function getUIConfigByMapName(mapName: string): {
  startModal: StartModalConfig;
  endScreen: EndScreenConfig;
  designer: string;
  i18nMapKey: MapI18nKey;
  apiMapType: string;
} {
  const entry = Object.values(GAME_REGISTRY).find(e => e.displayName === mapName);
  if (entry) {
    return {
      startModal: entry.startModalConfig,
      endScreen: entry.endScreenConfig,
      designer: entry.designer,
      i18nMapKey: entry.i18nMapKey,
      apiMapType: entry.apiMapType,
    };
  }
  // デフォルト: Barbados
  return {
    startModal: BARBADOS_START_MODAL_CONFIG,
    endScreen: BARBADOS_END_SCREEN_CONFIG,
    designer: 'Ted Alspach',
    i18nMapKey: 'barbados',
    apiMapType: 'barbados',
  };
}

/**
 * マップ名からi18nマップキーを取得
 * @param mapName - マップ表示名（G.mapName）
 * @returns i18nマップキー
 */
export function getI18nMapKeyByMapName(mapName: string): MapI18nKey {
  const entry = Object.values(GAME_REGISTRY).find(e => e.displayName === mapName);
  return entry?.i18nMapKey || 'barbados';
}
